import Vue from 'vue'
import {
  META_DESCRIPTION_PARAMS_REGEX,
  META_TITLE_PARAMS_REGEX,
  NO_DIRECTIVE
} from 'enums/seo'
import { removeEndingSlashes } from '~/plugins/helper'

const DEFAULT_SEO_SOURCE = 'seo/pageSeo'
const DEFAULT_OG_IMAGE_PATH = '/images/open-graph-image.jpg'

function getDefaultOgImageUrl() {
  const domain = removeEndingSlashes(process.env.DOMAIN_URL)
  return `${domain}${DEFAULT_OG_IMAGE_PATH}`
}

const generateMetaTags = function({
  titleParam = null,
  descriptionParam = null,
  additionalLinks = [],
  additionalMetaTags = [],
  uncrawlable = false,
  source = DEFAULT_SEO_SOURCE,
  script = [],
  disableSanitizersByTagIdOptions = {},
  canonicalUrlGenerator = () =>
    this.$helper.getPageCanonicalLink(this.$route.path)
} = {}) {
  const link = [...additionalLinks]
  const meta = []

  link.push({
    hid: 'canonical',
    rel: 'canonical',
    href: canonicalUrlGenerator()
  })

  /**
   *  Generate metatags
   **/

  const {
    MetaTitle,
    MetaDescription,
    Directives,
    Keyphrases
  } = this.$store.getters[source]

  const title = generateTitle(MetaTitle, titleParam)
  const description = generateDescription(MetaDescription, descriptionParam)
  const ogSiteName = this.$store.getters['seo/ogSiteName']
  const keywords = generateKeywords(Keyphrases)
  const isPreviewUrl = !!this.$route.query.previewId
  const robotDirectives = generateDirectives(
    Directives,
    isPreviewUrl || uncrawlable
  )

  meta.push(
    ...getMetaSettings(
      this.$helper.getFullPath(this),
      title,
      description,
      keywords,
      robotDirectives,
      ogSiteName
    ),
    ...additionalMetaTags
  )

  return {
    title,
    link,
    meta,
    __dangerouslyDisableSanitizersByTagID: {
      'page-schemas': ['innerHTML'],
      ...disableSanitizersByTagIdOptions
    },
    script
  }
}

function generateTitle(template, param) {
  return (template || '').replace(META_TITLE_PARAMS_REGEX, param) || param
}

function generateDescription(template, param) {
  return (template || '').replace(META_DESCRIPTION_PARAMS_REGEX, param) || param
}

function generateKeywords(keyphrases = []) {
  return keyphrases.map(({ Keyword }) => Keyword).join(', ')
}

function generateDirectives(str = '', isNoindex) {
  if (isNoindex) return 'noindex'

  return str === NO_DIRECTIVE ? '' : str.toLowerCase()
}

function generateNoindexNofollowMetaTags({
  titleParam = null,
  descriptionParam = null,
  source = null,
  staticTitle = null,
  staticDescription = null
} = {}) {
  const { MetaTitle, MetaDescription } = this.$store.getters[
    source || DEFAULT_SEO_SOURCE
  ]
  const meta = [
    {
      hid: 'directives',
      name: 'robots',
      content: 'noindex, nofollow'
    }
  ]

  let title

  if (titleParam || staticTitle) {
    title = staticTitle || generateTitle(MetaTitle, titleParam)
  }

  if (descriptionParam || staticDescription) {
    const descriptionMeta = {
      hid: 'description',
      name: 'description',
      content:
        staticDescription ||
        generateDescription(MetaDescription, descriptionParam)
    }

    meta.push(descriptionMeta)
  }

  return {
    ...(title ? { title } : {}),
    meta
  }
}

const createMeta = {
  install(Vue) {
    Vue.prototype.$generateMetaTags = generateMetaTags
    Vue.prototype.$generateNoindexNofollowMetaTags = generateNoindexNofollowMetaTags
  }
}

function generateThumbnailMetaSettings(ogImageUrl) {
  return [
    { hid: 'og-image', property: 'og:image', content: ogImageUrl },
    {
      hid: 'og:image:secure_url',
      property: 'og:image',
      content: ogImageUrl
    },
    {
      hid: 'twitter-image',
      name: 'twitter:image',
      content: ogImageUrl
    }
  ]
}

function getMetaSettings(
  fullPath,
  title,
  description,
  keywords,
  robotDirectives,
  ogSiteName,
  ogImageUrl = getDefaultOgImageUrl()
) {
  return [
    { hid: 'description', name: 'description', content: description },
    ...(keywords
      ? [{ hid: 'keywords', name: 'keywords', content: keywords }]
      : []),
    { hid: 'directives', name: 'robots', content: robotDirectives },
    {
      hid: 'apple-mobile-web-app-title',
      name: 'apple-mobile-web-app-title',
      content: title
    },
    { hid: 'og-title', property: 'og:title', content: title },
    {
      hid: 'og-description',
      property: 'og:description',
      content: description
    },
    { hid: 'og-type', property: 'og:type', content: 'website' },
    {
      hid: 'og-url',
      property: 'og:url',
      content: fullPath
    },
    {
      hid: 'og-site-name',
      property: 'og:site_name',
      content: ogSiteName
    },
    {
      hid: 'twitter-title',
      name: 'twitter:title',
      content: title
    },
    {
      hid: 'twitter-description',
      name: 'twitter:description',
      content: description
    },
    ...generateThumbnailMetaSettings(ogImageUrl)
  ]
}

Vue.use(createMeta)
